/**
 * Auth SDK Service - Type-safe OAuth2 authentication using generated SDK
 *
 * This service wraps the generated TypeScript SDK (from openapi-typescript-codegen)
 * and integrates it with the existing doc2app authentication flow.
 *
 * Key features:
 * - Type-safe OAuth2 operations with compile-time checking
 * - Automatic token management via OpenAPI.TOKEN configuration
 * - Integration with Pinia stores (useAuthTokenStore, useUserStore)
 * - RFC 6749 compliant OAuth2 implementation
 *
 * @see /src/generated-auth-sdk - Generated from auth service OpenAPI spec
 */

import {
  OpenAPI,
  Oauth2V2Service,
  AuthService,
  OAuth2TokenRequest,
} from 'src/generated-auth-sdk';
import type {
  OAuth2TokenResponse,
  OAuth2ProfileResponse,
  SetSessionUserRequest,
  SetSessionUserResponse,
} from 'src/generated-auth-sdk';
import { useAuthTokenStore } from 'src/store/User/auth_token.store';
import { useUserStore } from 'src/store/User/user_details.store';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { LocalStorage, SessionStorage } from 'quasar';
import { AUTH_URL } from 'src/pages/APIs';

/**
 * Configure SDK with auth service URL and token management
 */
const initializeSdk = () => {
  // ✅ CRITICAL FIX: Use AUTH_URL from APIs.ts which respects APP_VUE_STAGE=local
  // This ensures the SDK uses localhost:5001 when testing locally
  const authUrl = AUTH_URL;

  OpenAPI.BASE = authUrl;

  // Configure credentials for cross-origin requests
  OpenAPI.WITH_CREDENTIALS = true;
  OpenAPI.CREDENTIALS = 'include';

  // Dynamic token resolver - fetches token from store when needed
  // IMPORTANT: Strip "Bearer " prefix if present because the SDK's request.ts
  // always adds "Bearer " in getHeaders(). The store's token field can contain
  // either a raw token or "Bearer <token>" depending on whether setAuthToken()
  // or setTokenInLocalStorage() was called last.
  OpenAPI.TOKEN = async () => {
    const authStore = useAuthTokenStore();
    const token = authStore.token || '';
    return token.startsWith('Bearer ') ? token.slice(7) : token;
  };
};

// Initialize on module load
initializeSdk();

/**
 * SDK-based Auth Service
 *
 * Provides type-safe OAuth2 authentication methods using the generated SDK.
 * Maintains compatibility with existing authApiService.ts interface.
 */
export class AuthSdkService {
  /**
   * Refresh OAuth2 access token using refresh token
   *
   * @param refreshToken - The refresh token from previous authentication
   * @returns OAuth2 token response with new access_token and refresh_token
   *
   * @example
   * ```typescript
   * const response = await authSdkService.refreshToken('tFnANP87rRMSCCuSwTT0Y5M0LmpUmPMbBow2F8zKCM');
   * // response.access_token, response.refresh_token, response.user_id
   * ```
   */
  public async refreshToken(
    refreshToken: string,
  ): Promise<OAuth2TokenResponse> {
    // ✅ RFC 6749 Compliance: Use public client with client_id parameter
    // This fixes the "invalid_client" error: "Could not extract client_id from Authorization header or form data"
    const request: OAuth2TokenRequest = {
      grant_type: 'refresh_token' as OAuth2TokenRequest.grant_type,
      refresh_token: refreshToken,
      client_id: 'frontend',
    };

    try {
      const response = await Oauth2V2Service.postV2Oauth2Token({
        formData: request,
      });

      // Type guard: ensure we got a successful response, not an error
      if ('access_token' in response) {
        const tokenResponse = response as OAuth2TokenResponse;

        // If user_id not in response, try to extract from JWT access_token
        if (!tokenResponse.user_id && tokenResponse.access_token) {
          try {
            const tokenParts = tokenResponse.access_token.split('.');
            if (tokenParts.length === 3) {
              const decoded = JSON.parse(atob(tokenParts[1]));
              tokenResponse.user_id =
                decoded.user_id || decoded.sub || decoded.id;
            }
          } catch {
            // Failed to decode JWT, that's okay - user_id might be null
          }
        }

        return tokenResponse;
      }

      throw new Error('Token refresh failed');
    } catch (error: any) {
      // Enhanced error logging for debugging intermittent refresh failures
      console.error('[AUTH_SDK] ❌ refreshToken error details:', {
        errorType: error?.constructor?.name || typeof error,
        body: error.body,
        message: error.message,
        status: error.status,
        statusCode: error.statusCode,
        response: error.response,
        // OAuth2 specific error fields
        error_code: error.body?.error || error.error,
        error_description: error.body?.error_description || error.error_description,
      });

      // Check for specific OAuth2 errors that indicate token reuse/replay
      const errorCode = error.body?.error || error.error;
      if (errorCode === 'invalid_grant' || errorCode === 'refresh_token_replay') {
        console.error('[AUTH_SDK] ⚠️ RACE CONDITION DETECTED: refresh_token was likely already used');
        console.error('[AUTH_SDK] This happens when multiple concurrent requests trigger refresh simultaneously');
      }

      throw error.body || error.message || error;
    }
  }

  /**
   * Authenticate user with username/password (OAuth2 password grant)
   *
   * @param username - User email
   * @param password - User password
   * @param scope - OAuth2 scope (default: 'profile')
   * @returns OAuth2 token response with access_token, refresh_token, and user_id
   *
   * @example
   * ```typescript
   * const response = await authSdkService.login('user@example.com', 'secret', 'profile');
   * // Store tokens and user_id for session establishment
   * ```
   */
  public async login(
    username: string,
    password: string,
    scope = 'profile offline_access', // Include offline_access to get refresh_token (DOCB-9171)
  ): Promise<OAuth2TokenResponse> {
    // ✅ FIX: RFC 6749 compliance - Use public client with client_id parameter
    // This fixes the "invalid_client" error: "Could not extract client_id from Authorization header or form data"
    // OAuth2 password grant requires client identification - using public client 'frontend' (no secret)
    const request: OAuth2TokenRequest = {
      grant_type: OAuth2TokenRequest.grant_type.PASSWORD,
      username,
      password,
      scope,
      client_id: 'frontend',
    };

    try {
      const response = await Oauth2V2Service.postV2Oauth2Token({
        formData: request,
      });

      if ('access_token' in response) {
        const tokenResponse = response as OAuth2TokenResponse;

        // If user_id not in response, try to extract from JWT access_token
        if (!tokenResponse.user_id && tokenResponse.access_token) {
          try {
            const tokenParts = tokenResponse.access_token.split('.');
            if (tokenParts.length === 3) {
              const decoded = JSON.parse(atob(tokenParts[1]));
              tokenResponse.user_id =
                decoded.user_id || decoded.sub || decoded.id;
            }
          } catch {
            // Failed to decode JWT, that's okay - user_id might be null
          }
        }

        return tokenResponse;
      }

      throw new Error('Login failed');
    } catch (error: any) {
      throw error.body || error.message || error;
    }
  }

  /** POST /v2/oauth2/set_session_user and persist the resulting org_id. */
  public async setUserSession(
    userId?: string,
    orgId?: string,
  ): Promise<SetSessionUserResponse> {
    const userStore = useUserStore();
    const authStore = useAuthTokenStore();

    // Hydrate Pinia state from LocalStorage so the SDK's token resolver
    // sees the latest token even if Login just wrote it synchronously.
    authStore.hydrateFromLocalStorage();

    let effectiveUserId = userId || userStore.getUserDetailByKey('id');
    if (!effectiveUserId) {
      // Fresh login flow: Login.vue's setTokenInLocalStorage() writes the entire
      // OAuth2 token response (including user_id) to LocalStorage.token_details.
      const tokenDetails = LocalStorage.getItem('token_details') as any;
      if (tokenDetails?.user_id) {
        effectiveUserId = String(tokenDetails.user_id);
      }
    }

    if (!effectiveUserId) {
      throw new Error('user_id is required for session establishment.');
    }

    const effectiveOrgId = orgId || getOrgId();
    const request: SetSessionUserRequest = { user_id: Number(effectiveUserId) };
    if (effectiveOrgId) {
      request.org_id = effectiveOrgId;
    }

    let response: SetSessionUserResponse;
    try {
      const rawResponse = await Oauth2V2Service.postV2Oauth2SetSessionUser({
        requestBody: request,
      });
      if (rawResponse instanceof Error) {
        throw rawResponse;
      }
      response = rawResponse as SetSessionUserResponse;
    } catch (error: any) {
      const errorMessage = error?.body?.message || error?.message || String(error);
      if (
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control') ||
        errorMessage.includes('Failed to fetch') ||
        error?.status === 0
      ) {
        throw new Error('Session establishment failed due to CORS error.');
      }
      throw error?.body || error?.message || error;
    }

    const sessionOrgId = response.org_id || effectiveOrgId;
    if ('user_id' in response && sessionOrgId) {
      let userDetails = LocalStorage.getItem('user_details') as any;
      if (typeof userDetails === 'string') {
        try { userDetails = JSON.parse(userDetails); } catch { userDetails = {}; }
      }
      userDetails = userDetails || {};
      userDetails.org_id = String(sessionOrgId);
      LocalStorage.set('user_details', userDetails);
      SessionStorage.set('active_org_id', String(sessionOrgId));
    }

    return response;
  }

  /**
   * Get complete user profile with organizations and subscription details
   *
   * Returns comprehensive user information including:
   * - User basic information (id, email, name, etc.)
   * - All organizations the user belongs to
   * - Sub-organizations within those organizations
   * - Active subscription details
   * - Total sub-organization count
   *
   * ⚠️ CORS Note: This endpoint requires credentials mode and proper CORS configuration
   * on the auth service backend. If CORS errors occur, check that the backend has:
   * - Access-Control-Allow-Credentials: true
   * - Specific origins (not wildcard *) in Access-Control-Allow-Origin
   *
   * @returns Complete user profile
   * @throws Error if profile retrieval fails after retries
   *
   * @example
   * ```typescript
   * const profile = await authSdkService.getUserProfile();
   * // profile.user, profile.organisations, profile.subscription
   * ```
   */
  public async getUserProfile(
    retryCount = 0,
    maxRetries = 2,
  ): Promise<OAuth2ProfileResponse> {
    try {
      console.log(
        '[AUTH_SDK] getUserProfile: Fetching profile endpoint (credentials mode)...',
      );
      const response = await Oauth2V2Service.getV2Oauth2Profile();

      // DEBUG: Log the actual response structure to diagnose format mismatch
      console.log('[AUTH_SDK] Raw profile response:', JSON.stringify(response, null, 2));
      console.log('[AUTH_SDK] Response keys:', Object.keys(response));

      if ('user' in response) {
        console.log('[AUTH_SDK] ✓ Profile fetch successful');
        return response as OAuth2ProfileResponse;
      }

      // If no 'user' field, log the actual structure for debugging
      console.error('[AUTH_SDK] Profile response missing "user" field');
      console.error('[AUTH_SDK] Response structure:', response);

      throw new Error('Profile retrieval failed - invalid response format');
    } catch (error: any) {
      const errorMessage =
        error.body?.message || error.message || String(error);
      const isCorsError =
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control') ||
        errorMessage.includes('Failed to fetch') ||
        error.status === 0; // Network error often indicates CORS

      console.log(
        `[AUTH_SDK] ❌ Profile fetch error (attempt ${retryCount + 1}/${maxRetries + 1}):`,
        {
          isCorsError,
          message: errorMessage,
          status: error.status,
        },
      );

      // Retry on transient errors (CORS, network) but not on auth errors (401, 403)
      if (isCorsError && retryCount < maxRetries) {
        console.log(
          `[AUTH_SDK] Retrying profile fetch in 1 second... (${retryCount + 1}/${maxRetries})`,
        );
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.getUserProfile(retryCount + 1, maxRetries);
      }

      // If CORS error after retries, provide helpful guidance
      if (isCorsError) {
        const corsHint =
          '\n⚠️  CORS Configuration Issue:\n' +
          '  - Backend is missing: Access-Control-Allow-Credentials: true\n' +
          '  - Backend should use specific origins, not wildcard (*)\n' +
          '  - Issue is in cloudintegration_subscription/config.py CORS settings';
        console.error('[AUTH_SDK] CORS Error Details:', corsHint);
        throw new Error(
          `Profile retrieval failed due to CORS error. ${corsHint}`,
        );
      }

      // For other errors, re-throw with more context
      throw error.body || error.message || error;
    }
  }

  /**
   * Update SDK configuration (base URL, credentials, token resolver)
   *
   * @param config - Partial OpenAPI configuration to update
   *
   * @example
   * ```typescript
   * authSdkService.configure({
   *   BASE: 'https://stage.auth.docbits.com',
   *   TOKEN: async () => customTokenGetter()
   * });
   * ```
   */
  public configure(config: Partial<typeof OpenAPI>): void {
    Object.assign(OpenAPI, config);
  }

  /**
   * Get current SDK configuration
   *
   * @returns Current OpenAPI configuration
   */
  public getConfig(): typeof OpenAPI {
    return OpenAPI;
  }
}

// Export singleton instance
export const authSdkService = new AuthSdkService();

// Export for default import compatibility
export default authSdkService;
